import { type ReactNode, useId } from "react";
import {
  Frame,
  FrameDescription,
  FrameFooter,
  FrameHeader,
  FramePanel,
} from "@volca/ui/components/ui/frame";
import { Stack } from "@volca/ui/components/ui/stack";
import { Heading, Text } from "@volca/ui/components/ui/text";

export const SettingsGroup = ({
  title,
  description,
  footer,
  children,
}: {
  title: string;
  description?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) => {
  const id = useId();
  return (
    <Frame role="region" aria-labelledby={id}>
      <FrameHeader className="gap-1 py-3">
        <Heading id={id} level={2} variant="title">
          {title}
        </Heading>
        {description && (
          <FrameDescription className="max-w-prose text-pretty [overflow-wrap:anywhere]">
            {description}
          </FrameDescription>
        )}
      </FrameHeader>
      <FramePanel className="divide-y divide-line overflow-visible p-0">
        {children}
      </FramePanel>
      {footer && (
        <FrameFooter className="py-2">
          <Text
            variant="caption"
            tone="muted"
            className="max-w-prose text-pretty [overflow-wrap:anywhere]"
          >
            {footer}
          </Text>
        </FrameFooter>
      )}
    </Frame>
  );
};

export const SettingsRow = ({
  label,
  value,
  hint,
  action,
}: {
  label: string;
  value?: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
}) => (
  <Stack gap={3} className="p-4 sm:flex-row sm:items-start sm:justify-between">
    <Stack gap={1} className="min-w-0 flex-1 [overflow-wrap:anywhere]">
      <Text variant="label">{label}</Text>
      {value !== undefined && (
        <Stack className="min-w-0 text-sm text-ink">{value}</Stack>
      )}
      {hint && (
        <Text variant="caption" tone="muted" className="text-pretty">
          {hint}
        </Text>
      )}
    </Stack>
    {action && <Stack className="w-full shrink-0 sm:w-auto">{action}</Stack>}
  </Stack>
);
